import { CustomRolesCard } from "@/components/teams/CustomRolesCard";
import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2, MessageCircle, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { InviteMemberDialog } from "@/components/teams/InviteMemberDialog";
import { InvitationsTable } from "@/components/teams/InvitationsTable";
import { MembersTable } from "@/components/teams/MembersTable";
import { TeamSettingsCard } from "@/components/teams/TeamSettingsCard";
import { useAuthUser } from "@/context/AuthContext";
import { useInvitations, useTeam } from "@/lib/api/teams.queries";

export const Route = createFileRoute("/teams/$teamId")({
  component: TeamDetailPage,
});

function TeamDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TeamDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

const STATUS_VARIANT: Record<string, "default" | "muted" | "secondary" | "warning"> = {
  active: "default",
  trialing: "warning",
  suspended: "muted",
  archived: "muted",
};

function TeamDetailContent() {
  const { teamId } = Route.useParams();
  const { t } = useTranslation();
  const me = useAuthUser();
  const teamQuery = useTeam(teamId);
  const [inviteOpen, setInviteOpen] = useState(false);

  if (teamQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (teamQuery.isError || !teamQuery.data) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("teams.detail.error.title")}
          description={t("teams.detail.error.body")}
          onRetry={() => void teamQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      </div>
    );
  }

  const team = teamQuery.data;
  const meMembership = team.members.find((m) => m.userId === me.id);
  const myRole = meMembership?.role;
  const isAdminOrOwner = myRole === "owner" || myRole === "admin";
  const statusVariant = STATUS_VARIANT[team.status] ?? "muted";

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Link
          to="/teams"
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("teams.detail.back")}
        </Link>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {team.name}
              </h1>
              <Badge variant={statusVariant}>
                {t(`teams.status.${team.status}`, team.status)}
              </Badge>
            </div>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {team.slug}
            </p>
          </div>
          {isAdminOrOwner ? (
            <Button onClick={() => setInviteOpen(true)}>
              <MessageCircle className="h-4 w-4" />
              {t("invitations.invite.cta")}
            </Button>
          ) : null}
        </div>
      </div>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members" className="gap-2">
            <Users className="h-3.5 w-3.5" />
            {t("teams.detail.tab.members")}
            <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
              {team.members.length}
            </span>
          </TabsTrigger>
          {isAdminOrOwner ? (
            <TabsTrigger value="invitations" className="gap-2">
              <MessageCircle className="h-3.5 w-3.5" />
              {t("teams.detail.tab.invitations")}
              {team.pendingInvitationsCount > 0 ? (
                <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                  {team.pendingInvitationsCount}
                </span>
              ) : null}
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="settings">
            {t("teams.detail.tab.settings")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members">
          <Card>
            <CardContent className="p-0">
              <MembersTable team={team} />
            </CardContent>
          </Card>
        </TabsContent>

        {isAdminOrOwner ? (
          <TabsContent value="invitations">
            <InvitationsTab teamId={team.id} />
          </TabsContent>
        ) : null}

        <TabsContent value="settings">
          {isAdminOrOwner ? <CustomRolesCard teamId={teamId} /> : null}
          <TeamSettingsCard team={team} isAdminOrOwner={isAdminOrOwner} />
        </TabsContent>
      </Tabs>

      <InviteMemberDialog
        teamId={team.id}
        teamName={team.name}
        open={inviteOpen}
        onOpenChange={setInviteOpen}
      />
    </div>
  );
}

function InvitationsTab({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const invitationsQuery = useInvitations(teamId);

  if (invitationsQuery.isPending) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (invitationsQuery.isError) {
    return (
      <ErrorState
        title={t("invitations.error.title")}
        description={t("invitations.error.body")}
        onRetry={() => void invitationsQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const invitations = invitationsQuery.data ?? [];

  return (
    <Card>
      <CardContent className="p-0">
        <InvitationsTable teamId={teamId} invitations={invitations} />
      </CardContent>
    </Card>
  );
}
